import {
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { State } from '../../shared/core/state';
import { Store } from '@ngrx/store';

import * as LiveClassActions from '../../shared/core/state/live-class/live-class.action';
import {
  LiveClass,
  LiveClassResponseDTO,
  Meeting,
} from '../../shared/core/state/live-class/models/live-class';
import { Resource } from '../../shared/core/state/resource/models';
import { getResourcePath } from '../../shared/util/helper.utils';

import { MeetingUtility } from '../../shared/core/state/live-class/models/live-class.dto';
import { Meta, Title } from '@angular/platform-browser';
import { DateTime } from 'luxon';
import {
  PaymentType,
  Subscription,
  SubscriptionStatus,
} from '../../shared/core/state/subscription/subscription.model';
import { FormatterService } from '../../shared/util/formatter.service';
import {
  getNextSessionEventDate,
  getEffectiveMeetingAccess,
  MeetingAccessResult,
} from '@black/shared';
import { TenantService } from '../../shared/services/tenant.service';

@Component({
  selector: 'black-live-class-details',
  templateUrl: './live-class-details.component.html',
  styleUrls: ['./live-class-details.component.css'],
  standalone: false,
})
export class LiveClassDetailsComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private store = inject<Store<State>>(Store);
  private titleService = inject(Title);
  private metaService = inject(Meta);
  private formatter = inject(FormatterService);
  private tenantService = inject(TenantService);

  selectedLiveClass: LiveClassResponseDTO | null;

  constructor() {
    this.selectedLiveClass = null;
  }

  ngOnDestroy(): void {
    this.tenantService.updateDocumentTitle();
  }

  ngOnInit(): void {
    const liveClassId = this.route.snapshot.paramMap.get('id');

    if (liveClassId) {
      this.store.dispatch(
        LiveClassActions.selectLiveClass({ id: liveClassId })
      );
    } else {
      this.router.navigate(['/live-classes']);
    }

    this.store
      .select((state) => state.liveClass.selectedLiveClassItem)
      .subscribe((item) => {
        if (item) {
          this.selectedLiveClass = item;
          this.setSEOData(
            this.selectedLiveClass.title,
            this.selectedLiveClass.details.descriptionSummary,
            this.getResourcePath(
              this.selectedLiveClass.settings.bannerImageResource
            )
          );
        }
      });
  }

  setSEOData(title: string, description: string, imgUrl: string) {
    const brand = this.tenantService.getTenantBrandName();
    const fullTitle = `${title} | ${brand}`;
    this.titleService.setTitle(fullTitle);
    this.metaService.updateTag({ name: 'description', content: description });

    this.metaService.addTags([
      { name: 'og:title', content: fullTitle },
      { name: 'og:description', content: description },
      { name: 'og:type', content: 'website' },
      { name: 'og:url', content: this.router.url },
      { name: 'og:image', content: imgUrl },
    ]);
  }

  getResourcePath = (resource: Resource | string) => {
    return getResourcePath(resource);
  };

  // meetings helpers

  getMeetingDescription(meeting: Meeting, startDate: Date, endDate: Date) {
    const meetingUtility = new MeetingUtility(meeting, startDate, endDate);
    return meetingUtility.toDescription();
  }

  getStartDate(meeting: Meeting, startDate: Date, endDate: Date) {
    const meetingUtility = new MeetingUtility(meeting, startDate, endDate);

    const startDateTime = meetingUtility.getStartDate();
    const intervalInMinutes = meetingUtility.getSessionInterval();
    const endDateTime = DateTime.fromJSDate(startDateTime)
      .plus({ minutes: intervalInMinutes })
      .toJSDate();

    return [startDateTime, endDateTime];
  }

  getMeetingAccess(meeting: Meeting): MeetingAccessResult {
    const nextDate = this.getNextMeetingDate(meeting);
    return getEffectiveMeetingAccess({
      subscriptions: this.selectedLiveClass?.subscriptions,
      meeting,
      targetDate: nextDate,
      now: new Date(),
    });
  }

  getMeetingInterval(meeting: Meeting, startDate: Date, endDate: Date) {
    const meetingUtility = new MeetingUtility(meeting, startDate, endDate);
    const diffInMinutes = meetingUtility.getSessionInterval();
    if (diffInMinutes < 60) return `${diffInMinutes} Minutes`;
    return `${Math.floor(diffInMinutes / 60)} hour ${
      diffInMinutes % 60
    } Minutes`;
  }

  subscribeNow() {
    this.router.navigate([
      'live-classes',
      this.selectedLiveClass?.id,
      'subscribe',
    ]);
  }

  get instructor() {
    return this.selectedLiveClass?.user ?? null;
  }

  continueLearning() {
    this.router.navigate([
      'my-learnings',
      'live-class',
      this.selectedLiveClass?.id,
    ]);
  }

  // this function is used to check if the user has a valid active subscription
  hasValidSubscription(liveClass: LiveClass) {
    const subscription = liveClass.subscriptions;
    return subscription.some((sub) =>
      [
        SubscriptionStatus.ACTIVE,
        SubscriptionStatus.COMPLETE,
        SubscriptionStatus.RENEWAL_DUE,
      ].includes(sub.status)
    );
  }

  get pendingSubscription(): Subscription | null {
    if (
      !this.selectedLiveClass?.subscriptions ||
      !Array.isArray(this.selectedLiveClass.subscriptions)
    ) {
      return null;
    }
    return (
      this.selectedLiveClass.subscriptions.find(
        (sub) => sub.status === SubscriptionStatus.PENDING
      ) || null
    );
  }

  isPendingPayment(): boolean {
    return !!this.pendingSubscription;
  }

  isPendingMeeting(meeting: Meeting): boolean {
    return this.getMeetingAccess(meeting).isPending;
  }

  get hasFullCourseAccess(): boolean {
    if (
      !this.selectedLiveClass?.subscriptions ||
      !Array.isArray(this.selectedLiveClass.subscriptions)
    ) {
      return false;
    }
    return this.selectedLiveClass.subscriptions.some(
      (sub) =>
        sub.paymentType !== PaymentType.SINGLE_EVENT &&
        [SubscriptionStatus.ACTIVE, SubscriptionStatus.COMPLETE].includes(sub.status)
    );
  }

  getSinglePassForMeeting(meeting: Meeting): Subscription | null {
    const access = this.getMeetingAccess(meeting);
    if (access.accessType === 'SINGLE_EVENT') {
      return access.effectiveSubscription || null;
    }
    return null;
  }

  get activeSubscription(): Subscription | null {
    if (
      !this.selectedLiveClass?.subscriptions ||
      !Array.isArray(this.selectedLiveClass.subscriptions)
    ) {
      return null;
    }
    return (
      this.selectedLiveClass.subscriptions.find((sub) =>
        [SubscriptionStatus.ACTIVE, SubscriptionStatus.COMPLETE].includes(
          sub.status
        )
      ) || null
    );
  }

  isSingleEventSubscriber(): boolean {
    return (
      !this.hasFullCourseAccess &&
      Array.isArray(this.selectedLiveClass?.subscriptions) &&
      this.selectedLiveClass!.subscriptions.some(
        (sub) =>
          sub.paymentType === PaymentType.SINGLE_EVENT &&
          [SubscriptionStatus.ACTIVE, SubscriptionStatus.COMPLETE].includes(sub.status)
      )
    );
  }

  isFreeMeeting(meeting: Meeting): boolean {
    return (
      meeting.price === 0 ||
      (meeting.price == null && (this.selectedLiveClass?.settings?.price ?? 0) === 0)
    );
  }

  isMeetingInPass(meeting: Meeting): boolean {
    return this.getMeetingAccess(meeting).hasAccess;
  }

  getTargetEventDate(meeting?: Meeting): Date | null {
    if (!meeting) return null;
    const access = this.getMeetingAccess(meeting);
    return access.singlePassDate || null;
  }

  isPassModalOpen = false;
  selectedPassDetails: {
    liveClassName?: string;
    liveClassId?: string;
    meetingName?: string;
    targetEventDate?: Date;
    startTime?: string;
    endTime?: string;
    directJoinUrl?: string;
    shareLinkId?: string;
    provider?: string;
    status: string;
    meeting?: Meeting;
  } | null = null;

  openPassModal(meeting: Meeting) {
    const access = this.getMeetingAccess(meeting);
    this.selectedPassDetails = {
      liveClassName: this.selectedLiveClass?.title,
      liveClassId: this.selectedLiveClass?.id,
      meetingName: meeting.name,
      targetEventDate: access.singlePassDate || undefined,
      startTime: meeting.startTime,
      endTime: meeting.endTime,
      directJoinUrl: meeting.directJoinUrl,
      shareLinkId: meeting.shareLinkId,
      provider: meeting.provider,
      status: 'Active Single Pass',
      meeting,
    };
    this.isPassModalOpen = true;
  }

  closePassModal() {
    this.isPassModalOpen = false;
    this.selectedPassDetails = null;
  }

  getNextMeetingDate(meeting: Meeting): Date | null {
    return getNextSessionEventDate(meeting as any, { now: new Date() });
  }

  getCategoryName = this.formatter.getCategoryName.bind(this.formatter);
}
